import * as React from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { patchJSON } from '@/lib/api'
import { apiMessage } from '@/lib/contacts/errors'
import { CloseRegistrationControl } from '@/components/events/close-registration-control'

interface EventRegistration {
  id: number
  registration_closes_at_local: string | null
  day_before_reminder_enabled: boolean
  week_before_reminder_enabled?: boolean
  pay_later_enabled: boolean
  cancellation_cutoff_days?: number
  cancellation_refund_outcome?: 'refund_to_source' | 'account_credit'
}

export function EventRegistrationForm({ event }: { event: EventRegistration }) {
  const queryClient = useQueryClient()
  const [registrationClosesAt, setRegistrationClosesAt] = React.useState(
    event.registration_closes_at_local?.slice(0, 16) ?? '',
  )
  const [dayBeforeReminderEnabled, setDayBeforeReminderEnabled] = React.useState(
    event.day_before_reminder_enabled,
  )
  const [weekBeforeReminderEnabled, setWeekBeforeReminderEnabled] = React.useState(
    event.week_before_reminder_enabled ?? false,
  )
  const [payLaterEnabled, setPayLaterEnabled] = React.useState(event.pay_later_enabled)
  const [cancellationCutoffDays, setCancellationCutoffDays] = React.useState(
    String(event.cancellation_cutoff_days ?? 0),
  )
  const [cancellationRefundOutcome, setCancellationRefundOutcome] = React.useState(
    event.cancellation_refund_outcome ?? 'refund_to_source',
  )
  const cancellationCutoff = cancellationCutoffDays === '' ? '' : Number(cancellationCutoffDays)
  const update = useMutation({
    mutationFn: () =>
      patchJSON<{ event: EventRegistration }>(`/api/v1/events/${event.id}`, {
        event: {
          registration_closes_at: registrationClosesAt,
          day_before_reminder_enabled: dayBeforeReminderEnabled,
          week_before_reminder_enabled: weekBeforeReminderEnabled,
          pay_later_enabled: payLaterEnabled,
          cancellation_cutoff_days: cancellationCutoff,
          cancellation_refund_outcome: cancellationRefundOutcome,
        },
      }),
    onSuccess: ({ event: updated }) => {
      queryClient.setQueryData(['event', event.id], { event: updated })
      queryClient.invalidateQueries({ queryKey: ['events'] })
    },
  })

  return (
    <section className="space-y-2" aria-labelledby="event-registration-heading">
      <h2
        id="event-registration-heading"
        className="text-caption font-medium text-muted-foreground"
      >
        Registration
      </h2>
      <form
        className="divide-y divide-border rounded-md border border-border bg-card"
        onSubmit={(formEvent) => {
          formEvent.preventDefault()
          update.mutate()
        }}
      >
        <EventCancellationSettings
          cutoffDays={cancellationCutoffDays}
          refundOutcome={cancellationRefundOutcome}
          onCutoffDaysChange={setCancellationCutoffDays}
          onRefundOutcomeChange={setCancellationRefundOutcome}
        />
        <div className="grid gap-2 px-4 py-3 sm:grid-cols-[148px_minmax(0,1fr)] sm:items-start">
          <Label htmlFor="pay-later" className="pt-1.5 text-caption text-muted-foreground">
            Pay later
          </Label>
          <div className="flex items-center gap-2 py-1.5">
            <Checkbox
              id="pay-later"
              aria-label="Members may choose Invoice me"
              checked={payLaterEnabled}
              onCheckedChange={(checked) => setPayLaterEnabled(checked === true)}
            />
            <Label htmlFor="pay-later" className="text-caption text-muted-foreground">
              Members may choose “Invoice me”
            </Label>
          </div>
        </div>
        <ReminderToggle
          id="week-before-reminder"
          label="Week-before reminder"
          checked={weekBeforeReminderEnabled}
          onCheckedChange={setWeekBeforeReminderEnabled}
        />
        <ReminderToggle
          id="day-before-reminder"
          label="Day-before reminder"
          checked={dayBeforeReminderEnabled}
          onCheckedChange={setDayBeforeReminderEnabled}
        />
        <div className="grid gap-2 px-4 py-3 sm:grid-cols-[148px_minmax(0,1fr)] sm:items-start">
          <Label
            htmlFor="registration-closes-at"
            className="pt-1.5 text-caption text-muted-foreground"
          >
            Close registration
          </Label>
          <div className="min-w-0">
            <CloseRegistrationControl
              value={registrationClosesAt}
              onChange={setRegistrationClosesAt}
            />
            <p className="mt-1 text-caption text-muted-foreground">
              Otherwise open until the event ends
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3 px-4 py-3 sm:pl-[164px]">
          <Button type="submit" variant="outline" className="h-8" disabled={update.isPending}>
            {update.isPending ? 'Saving…' : 'Save registration'}
          </Button>
          {update.isSuccess && (
            <p className="text-caption text-muted-foreground">Registration saved</p>
          )}
          {update.error && <Alert>{apiMessage(update.error)}</Alert>}
        </div>
      </form>
    </section>
  )
}

function ReminderToggle({
  id,
  label,
  checked,
  onCheckedChange,
}: {
  id: string
  label: string
  checked: boolean
  onCheckedChange: (checked: boolean) => void
}) {
  return (
    <div className="grid gap-2 px-4 py-3 sm:grid-cols-[148px_minmax(0,1fr)] sm:items-start">
      <Label htmlFor={id} className="pt-1.5 text-caption text-muted-foreground">
        {label}
      </Label>
      <div className="py-1.5">
        <Checkbox
          id={id}
          aria-label={`Send ${label.toLowerCase()}`}
          checked={checked}
          onCheckedChange={(value) => onCheckedChange(value === true)}
        />
      </div>
    </div>
  )
}

function EventCancellationSettings({
  cutoffDays,
  refundOutcome,
  onCutoffDaysChange,
  onRefundOutcomeChange,
}: {
  cutoffDays: string
  refundOutcome: NonNullable<EventRegistration['cancellation_refund_outcome']>
  onCutoffDaysChange: (value: string) => void
  onRefundOutcomeChange: (
    value: NonNullable<EventRegistration['cancellation_refund_outcome']>,
  ) => void
}) {
  return (
    <>
      <div className="grid gap-2 px-4 py-3 sm:grid-cols-[148px_minmax(0,1fr)] sm:items-start">
        <Label
          htmlFor="cancellation-cutoff-days"
          className="pt-1.5 text-caption text-muted-foreground"
        >
          Self-service cancellation
        </Label>
        <div className="flex flex-wrap items-center gap-2 py-1.5 text-caption text-muted-foreground">
          <span>Buyers may cancel or reduce seats until</span>
          <Input
            id="cancellation-cutoff-days"
            aria-label="Days before the event"
            type="number"
            min="0"
            max="365"
            className="h-8 w-20 tabular-nums"
            value={cutoffDays}
            onChange={(inputEvent) => onCutoffDaysChange(inputEvent.target.value)}
          />
          <span>days before the event</span>
        </div>
      </div>
      <div className="grid gap-2 px-4 py-3 sm:grid-cols-[148px_minmax(0,1fr)] sm:items-start">
        <Label
          htmlFor="cancellation-refund-outcome"
          className="pt-1.5 text-caption text-muted-foreground"
        >
          Cancellation refunds
        </Label>
        <div>
          <Select
            value={refundOutcome}
            onValueChange={(value) => onRefundOutcomeChange(value as typeof refundOutcome)}
          >
            <SelectTrigger id="cancellation-refund-outcome" className="h-8 w-full sm:w-72">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="refund_to_source">Refund the original payment</SelectItem>
              <SelectItem value="account_credit">Hold as Account Credit</SelectItem>
            </SelectContent>
          </Select>
          <p className="mt-1 text-caption text-muted-foreground">
            Offline payments are always held as Account Credit.
          </p>
        </div>
      </div>
    </>
  )
}
